import * as React from "react";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import BootstrapModalForm from "components/bootstrap/BootstrapModalForm";
import { ConfigurationFormField, TitleField } from "components/configurationforms";
import { FIELD_TYPES_WITH_ENCRYPTION_SUPPORT } from "./types";
const defaultConfigFields = {};
const defaultCancelAction = () => {
};
const defaultInitialValues = {};
const ConfigurationForm = ({
  cancelAction = defaultCancelAction,
  configFields = defaultConfigFields,
  children = null,
  titleHelpText = "",
  initialShow = false,
  includeTitleField = true,
  submitAction,
  title = null,
  titleValue: initialTitleValue = "",
  typeName = void 0,
  values: initialValues = defaultInitialValues,
  wrapperComponent: WrapperComponent = BootstrapModalForm,
  submitButtonText = void 0
}, ref) => {
  const [showConfigurationModal, setShowConfigurationModal] = useState(initialShow);
  const [titleValue, setTitleValue] = useState(void 0);
  const [values, setValues] = useState(void 0);
  const [fieldStates, setFieldStates] = useState({});
  useEffect(() => {
    const defaultValues = {};
    if (configFields) {
      Object.keys(configFields).forEach((field) => {
        defaultValues[field] = configFields[field].default_value;
      });
    }
    setValues({ ...defaultValues, ...initialValues });
    setFieldStates({});
  }, [showConfigurationModal, configFields, initialValues]);
  useEffect(() => {
    setTitleValue(initialTitleValue);
  }, [initialTitleValue, showConfigurationModal]);
  const getFormData = () => {
    const data = {
      type: typeName,
      configuration: {}
    };
    if (includeTitleField) {
      data.title = titleValue;
    }
    Object.keys(configFields).forEach((fieldName) => {
      data.configuration[fieldName] = values[fieldName] === void 0 ? null : values[fieldName];
    });
    return data;
  };
  const sortByPosOrOptionality = (x1, x2) => {
    const DEFAULT_POSITION = 100;
    const x1pos = configFields[x1.name].position || DEFAULT_POSITION;
    const x2pos = configFields[x2.name].position || DEFAULT_POSITION;
    let diff = x1pos - x2pos;
    if (!diff) {
      const isOptionalToNumber = (optional) => optional ? 1 : 0;
      diff = isOptionalToNumber(configFields[x1.name].is_optional) - isOptionalToNumber(configFields[x2.name].is_optional);
    }
    if (!diff) {
      diff = x1.pos - x2.pos;
    }
    return diff;
  };
  const fieldIsEncrypted = (configField) => {
    const fieldSupportsEncryption = FIELD_TYPES_WITH_ENCRYPTION_SUPPORT.includes(
      configField.type
    );
    if (!fieldSupportsEncryption) {
      return false;
    }
    return configField.is_encrypted;
  };
  const handleEncryptedFieldsBeforeSubmit = (data) => {
    const configurationEntries = Object.entries(data.configuration).map(
      ([fieldName, fieldValue]) => {
        const configField = configFields[fieldName];
        const fieldState = fieldStates[fieldName];
        if (fieldIsEncrypted(configField) && !(fieldState == null ? void 0 : fieldState.dirty) && fieldValue && fieldValue.is_set !== void 0) {
          return [fieldName, { keep_value: true }];
        }
        return [fieldName, fieldValue];
      }
    );
    return { ...data, configuration: Object.fromEntries(configurationEntries) };
  };
  const handleCancel = () => {
    setShowConfigurationModal(false);
    if (cancelAction) {
      cancelAction();
    }
  };
  const save = () => {
    const data = getFormData();
    submitAction(handleEncryptedFieldsBeforeSubmit(data));
    setShowConfigurationModal(false);
  };
  useImperativeHandle(ref, () => ({
    open() {
      setShowConfigurationModal(true);
    },
    getValue() {
      return getFormData();
    }
  }));
  const handleTitleChange = (_, value) => {
    setTitleValue(value);
  };
  const handleChange = (field, value, dirty = true) => {
    setValues({ ...values, ...{ [field]: value } });
    setFieldStates({ ...fieldStates, ...{ [field]: { dirty } } });
  };
  const renderConfigField = (configField, key, autoFocus) => {
    var _a;
    if (!values) return null;
    const value = values[key];
    return /* @__PURE__ */ React.createElement(
      ConfigurationFormField,
      {
        key,
        typeName,
        configField,
        configKey: key,
        configValue: value,
        autoFocus,
        dirty: (_a = fieldStates[key]) == null ? void 0 : _a.dirty,
        onChange: handleChange
      }
    );
  };
  let shouldAutoFocus = true;
  let titleElement;
  if (includeTitleField) {
    titleElement = /* @__PURE__ */ React.createElement(
      TitleField,
      {
        key: `${typeName}-title`,
        typeName,
        value: titleValue,
        onChange: handleTitleChange,
        helpText: titleHelpText
      }
    );
    shouldAutoFocus = false;
  }
  const sortedConfigFieldKeys = Object.keys(configFields).map((name, pos) => ({ name, pos })).sort(sortByPosOrOptionality);
  const renderedConfigFields = sortedConfigFieldKeys.map((key) => {
    const configField = renderConfigField(configFields[key.name], key.name, shouldAutoFocus);
    if (shouldAutoFocus) {
      shouldAutoFocus = false;
    }
    return configField;
  });
  return /* @__PURE__ */ React.createElement(
    WrapperComponent,
    {
      show: showConfigurationModal,
      title,
      onCancel: handleCancel,
      onSubmitForm: save,
      submitButtonText
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement("input", { type: "hidden", name: "type", value: typeName }), children, titleElement, renderedConfigFields)
  );
};
export default forwardRef(ConfigurationForm);
